"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.leaveFrameWindow = leaveFrameWindow;
exports.dateInLeaveFrame = dateInLeaveFrame;
exports.rangeInLeaveFrame = rangeInLeaveFrame;
exports.offerableLeaveFrames = offerableLeaveFrames;
/**
 * ⚠️⚠️ **THE DEADLINE IS IN THE FOLLOWING YEAR, WHICH IS THE WHOLE OF THE CARRY RULE.** A type with
 * `carry_to_month = 3` means *last year's remainder survives until 31 March* — so the 2026 frame is
 * spendable through 31 March 2027 and dead on the 1st of April. A type that carries nothing dies on
 * 31 December of its own year.
 *
 * ⚠️ **`max5` and `full` share this window**, because the two differ in HOW MUCH carries and not in
 * how long it lasts — `leave-carry-over.service.ts` applies the quantity when it moves the balance,
 * and a five-day cap that expired on a different date would be a second rule nobody stated.
 *
 * ⚠️ **The last day of the month is computed from day zero of the NEXT one**, which is the only
 * spelling that gets February right in a leap year without a table.
 */
function leaveFrameWindow(year, carryOver, carryToMonth) {
    const from = `${String(year)}-01-01`;
    if (carryOver === 'none' || carryToMonth === null) {
        return { from, to: `${String(year)}-12-31` };
    }
    const end = new Date(Date.UTC(year + 1, carryToMonth, 0));
    return { from, to: end.toISOString().slice(0, 10) };
}
/** Whether one date falls inside the frame. ⚠️ Inclusive at both ends — 31 March IS the deadline. */
function dateInLeaveFrame(date, window) {
    return date >= window.from && date <= window.to;
}
/**
 * Whether a whole request can be charged to a frame.
 *
 * ⚠️⚠️ **EVERY DAY, NOT JUST THE FIRST.** A range that starts on 30 March and ends on 2 April
 * straddles a 31 March deadline, and charging all five days to the expiring frame because the first
 * one fitted is how somebody spends days that are gone. The calendar greys the far side for the
 * same reason, so the case is visible before it is refused.
 */
function rangeInLeaveFrame(fromDate, toDate, window) {
    return dateInLeaveFrame(fromDate, window) && dateInLeaveFrame(toDate, window);
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHICH FRAMES ARE WORTH OFFERING AT ALL — three tests, all of them.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * **Open** — its carry deadline is ahead of today. **Covering** — it contains every selected day.
 * **Non-empty** — it has a day left to spend. A frame that fails any one of the three is a year
 * somebody can select, spend a minute picking days against, and be refused for.
 *
 * ⚠️⚠️ **IT LIVES HERE BECAUSE THE PICKER AND THE CALENDAR ARE THE SAME RULE READ FROM EITHER
 * END.** Days first narrows the year list to the frames that cover them; year first greys the days
 * the frame cannot cover, with `dateInLeaveFrame`. Written twice they drift, and the drift is
 * invisible — both screens keep working and simply disagree about which afternoon is wasted.
 *
 * ⚠️⚠️ **`remaining` RETURNING `null` MEANS *WE CANNOT SAY*, NEVER *EMPTY*.** An unlimited type has
 * no frame to exhaust, a type with no quota row in a year has none there, and a read that failed
 * tells us nothing. A frame is dropped when it is KNOWN to be empty and never merely because
 * nobody looked — offering a year that turns out to be spent is a wasted minute, but hiding the
 * only year somebody can actually use is a screen they cannot get past at all.
 */
function offerableLeaveFrames(input) {
    return input.years.filter((year) => {
        const window = leaveFrameWindow(year, input.carryOver, input.carryToMonth);
        if (window.to < input.today)
            return false;
        /* ⚠️ BOTH ends — a range starting inside an expiring frame and finishing outside it is the
           case this file was written for, and charging it here spends days that are gone. */
        if (input.selection !== null &&
            !rangeInLeaveFrame(input.selection.from, input.selection.to, window)) {
            return false;
        }
        const left = input.remaining(year);
        return left === null || left > 0;
    });
}
//# sourceMappingURL=leave-frame.js.map